import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subscription, catchError, combineLatest, filter, finalize, of, take } from 'rxjs';
import { OrdinalPipe } from 'src/app/pipes/ordinal/ordinal.pipe';
import { SmallNumberPipe } from 'src/app/pipes/small-number/small-number.pipe';
import { AuthService } from 'src/app/services/auth-service/auth-service.service';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { LoadingBarService } from 'src/app/services/loading-bar/loading-bar.service';
import { MetaItemsNewService } from 'src/app/services/meta-items-new/meta-items-new.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { CategoriesMenuComponent } from 'src/app/shared/components/categories-menu/categories-menu.component';
import { ChartComponent } from 'src/app/shared/components/chart/chart.component';
import { CheckPriceButtonComponent } from 'src/app/shared/components/check-price-button/check-price-button.component';
import { CopyTextButtonComponent } from 'src/app/shared/components/copy-text-button/copy-text-button.component';
import { MetaItemsForm } from 'src/app/shared/components/forms/interfaces/meta-items-form';
import { ItemImageComponent } from 'src/app/shared/components/item-image/item-image.component';
import { getAllSlotTypes } from 'src/app/shared/constants/slot-types.constant';
import { ChartData } from 'src/app/shared/models/chart.models';
import { Item, ShopCategory } from 'src/app/shared/models/items.model';
import { FightSize, GetAllFightSizes } from 'src/app/shared/models/meta-models/fight-size';
import { GearUseRecordResponse } from 'src/app/shared/models/meta-models/gear-use-record-response';
import { GearUseSort, GetAllGearUseSorts } from 'src/app/shared/models/meta-models/gear-use-sort';
import { GetAllKillEventPlayerRoles, KillEventPlayerRole } from 'src/app/shared/models/meta-models/kill-event-player-role';
import { SlotType } from 'src/app/shared/models/slot-type.model';

@Component({
  selector: 'app-meta-tems-new',
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    ReactiveFormsModule,
    MatIconModule,
    FormsModule,
    NgxMatSelectSearchModule,
    MatSelectModule,
    MatButtonModule,
    MatTooltipModule,
    OrdinalPipe,
    SmallNumberPipe,
    MatProgressBarModule,
    MatTableModule,
    CopyTextButtonComponent,
    CheckPriceButtonComponent,
    MatInputModule,
    ChartComponent,
    ItemImageComponent,
    MatMenuModule,
    MatDividerModule,
    CategoriesMenuComponent,
  ],
  templateUrl: './meta-tems-new.component.html',
  styleUrl: './meta-tems-new.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetaTemsNewComponent implements OnInit, OnDestroy, AfterViewInit {
  // MARK: Signals
  selectedCategories = signal<Set<ShopCategory>>(new Set<ShopCategory>());

  // MARK: Form Configuration & Data
  form!: FormGroup<MetaItemsForm>;
  slotTypes = getAllSlotTypes();
  killEventPlayerRoles = GetAllKillEventPlayerRoles();
  fightSizes = GetAllFightSizes();
  tiers = [1, 2, 3, 4, 5, 6, 7, 8];
  enchantments = [0, 1, 2, 3, 4];
  sorts = GetAllGearUseSorts();

  // MARK: Component Data
  chartData: { data: ChartData[]; name: string }[] = [];

  // MARK: Table Configuration & Data
  displayedColumns: string[] = [
    'item',
    'totalItemCount',
    'totalPlayerCount',
    'totalEventCount',
    'totalKillerCount',
    'totalAssistantCount',
    'totalVictimCount',
    'killDeathRatio',
    'totalKillFame',
    'averageKillFame',
    'totalDeathFame',
    'averageDeathFame',
    'totalDamageDone',
    'averageDamageDone',
    'totalSupportHealingDone',
    'averageSupportHealingDone',
  ];
  dataSource = new MatTableDataSource<GearUseRecordResponse>([]);

  // MARK: Internal State
  private subscriptions: Subscription[] = [];

  // MARK: Constructor
  constructor(
    public userSettingsService: UserSettingsService,
    private metaItemsNewService: MetaItemsNewService,
    public jsonReaderService: JsonReaderService,
    public authService: AuthService,
    public LoadingBarService: LoadingBarService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = new FormGroup<MetaItemsForm>({
      server: new FormControl<string | null>(null),
      days: new FormControl<number | null>(null),
      limit: new FormControl<number | null>(null),
      slotTypes: new FormControl<SlotType[] | null>(null),
      killEventPlayerRoles: new FormControl<KillEventPlayerRole[] | null>(null),
      fightSizes: new FormControl<FightSize[] | null>(null),
      tiers: new FormControl<number[] | null>(null),
      enchantments: new FormControl<number[] | null>(null),
      minTotalItemCount: new FormControl<number | null>(0),
      sorts: new FormControl<GearUseSort[] | null>(null),
    });

    console.log('MetaTemsNewComponent initialized');
    this.jsonReaderService.ensureLoaded();
  }

  // MARK: Lifecycle Hooks
  ngOnInit(): void {
    this.subscriptions.push(
      //catch server changes
      this.userSettingsService.userServer$.subscribe((server) => {
        this.form.controls.server.setValue(server);
        this.update();
        this.cdr.markForCheck();
      }),
      this.authService.user$.pipe(filter((user) => !!user)).subscribe(() => {
        this.metaItemsNewService.getKillEventsCounters(28).subscribe((data) => {
          const serverGroups: { [key: string]: ChartData[] } = {};
          data.forEach((item) => {
            if (!serverGroups[item.server]) {
              serverGroups[item.server] = [];
            }
            serverGroups[item.server].push({
              date: item.dateTime.getTime(),
              value: item.eventCount,
            });
          });
          Object.entries(serverGroups).forEach(([server, items]) => {
            this.chartData.push({ data: items, name: server });
          });
          this.cdr.markForCheck();
        });
      })
    );
  }

  ngAfterViewInit() {
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  // MARK: Data Handling and Updates
  update(): void {
    if (!this.userSettingsService.isLoggedIn) {
      return;
    }
    this.dataSource.data = [];
    this.cdr.markForCheck();
    const metaObservable = this.metaItemsNewService
      .getMetaItems(
        this.form.value.server ?? this.userSettingsService.getUserServer(),
        undefined,
        undefined,
        this.form.value.slotTypes,
        Array.from(this.selectedCategories()),
        this.form.value.killEventPlayerRoles,
        this.form.value.fightSizes,
        this.form.value.tiers,
        this.form.value.enchantments,
        this.form.value.sorts,
        this.form.value.minTotalItemCount ?? 0
      )
      .pipe(
        take(1),
        finalize(() => {})
      );

    combineLatest([metaObservable, this.jsonReaderService.hasLoaded$])
      .pipe(
        filter(([data, hasLoaded]) => hasLoaded === true),
        catchError((error) => {
          console.error('Error occurred:', error);
          return of([]);
        })
      )
      .subscribe(([data, hasLoaded]) => {
        this.dataSource.data = data;
        this.cdr.markForCheck();
      });
  }

  // MARK: Utility Methods
  getCountString(control: FormControl, max: number): string {
    if (!control.value || control.value.length === 0) {
      return '(All)';
    }
    return `(${control.value.length}/${max})`;
  }

  noCompareFunction() {
    return 0;
  }

  metaFilter = (category: ShopCategory): boolean => {
    return true;
  };
}
